/**
 * Unified Proforma builder — used by every module (Retail, Restaurant,
 * Delivery) to print non-fiscal slips on a 76-mm thermal printer.
 *
 * Layout (Feb 2026 spec):
 *   1. Business info from Settings (each non-empty `business_lines`
 *      entry is centred; the bold flag controls the font weight + size)
 *   2. Big-font order CODE (skipped if missing)
 *   3. Combined «Αρ.Παρ: XXXX ΗΗ/ΜΜ/ΕΕ ΩΩ:ΛΛ» bold row (skipped if neither)
 *   4. Optional table / batch row (Restaurant)
 *   5. «ΣΤΟΙΧΕΙΑ ΠΕΛΑΤΗ» section — entire section skipped when no
 *      customer info is supplied. Inside the section each row is also
 *      skipped if its value is empty so we never print «Τηλέφωνο: —».
 *   6. Items list — bold item line with VAT label to the right of the
 *      price; secondary indented line shows `qty × unit_price` when
 *      qty > 1.
 *   7. ΣΥΝΟΛΟ
 *   8. Optional driver row
 *   9. «ΔΕΝ ΑΠΟΤΕΛΕΙ ΦΟΡΟΛΟΓΙΚΟ ΣΤΟΙΧΕΙΟ»
 *  10. «Powered by tameiakes.gr»
 *  11. CODE128 barcode (7777XXXX) — only if `code` is present
 *
 * The function returns the HTML string. Call `openProformaWindow(html,
 * barcodeValue)` to spawn the popup and trigger `window.print()`.
 */
import { printHtmlSilent } from "@/lib/kioskMode";
import { vatLabel } from "@/lib/api";

function esc(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * @param {object} ctx
 * @param {object|null} ctx.settings  Settings doc (business_lines, business_lines_bold)
 * @param {number|string|null} ctx.code  4-digit order code (proforma barcode)
 * @param {number|null} ctx.globalOrderNo  «Αρ. Παραγγελίας» cross-module counter
 * @param {string|null} ctx.createdAt  ISO datetime; falls back to now
 * @param {string|null} ctx.title  Optional top-line tag e.g. «ΔΙΑΝΟΜΗ», «ΕΣΤΙΑΤΟΡΙΟ»
 * @param {string|null} ctx.tableName  Restaurant only
 * @param {number|null} ctx.batchNo  Restaurant kitchen ticket batch
 * @param {string|null} ctx.customerName
 * @param {string|null} ctx.phone
 * @param {Array<{label:string,value:string}>} ctx.addressRows
 *        e.g. [{label:"Διεύθυνση", value:"…"}, {label:"Περιοχή", value:"…"}]
 * @param {string|null} ctx.notes  «Σχόλια Παραγγελίας»
 * @param {Array} ctx.items  Cart-line shape: {description, qty, price, comments?, free_text?}
 * @param {number} ctx.total
 * @param {string|null} ctx.driverName
 */
export function buildProformaHtml(ctx) {
  const {
    settings = null,
    code = null,
    globalOrderNo = null,
    createdAt = null,
    title = null,
    tableName = null,
    batchNo = null,
    customerName = null,
    phone = null,
    addressRows = [],
    notes = null,
    items = [],
    total = 0,
    driverName = null,
  } = ctx || {};

  const bizLines = (settings && settings.business_lines) || [];
  const bizBold = (settings && settings.business_lines_bold) || [];
  const businessHtml = bizLines
    .map((ln, i) => {
      const txt = (ln || "").trim();
      if (!txt) return "";
      const bold = bizBold[i];
      const size = bold ? 14 : 11;
      return `<div class="center ${bold ? "bold" : ""}" style="font-size:${size}px;line-height:1.25">${esc(txt)}</div>`;
    })
    .join("");

  const code4 = code != null && code !== "" ? String(code).padStart(4, "0") : null;
  const barcodeValue = code4 ? `7777${code4}` : null;
  // Compact datetime in ΗΗ/ΜΜ/ΕΕ ΩΩ:ΛΛ format. Used inside the bold
  // «Αρ.Παρ: XXXX ΗΗ/ΜΜ/ΕΕ ΩΩ:ΛΛ» combined row.
  const dt = (() => {
    try {
      const d = createdAt ? new Date(createdAt) : new Date();
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return "";
    }
  })();

  // ----- Customer block — every row optional ---------------------
  const custRows = [];
  if (customerName) custRows.push(`<div class="kv" style="font-size:14px">Όνομα: <span class="bold" style="font-size:15px">${esc(customerName)}</span></div>`);
  if (phone) custRows.push(`<div class="kv">Τηλέφωνο: ${esc(phone)}</div>`);
  for (const r of addressRows || []) {
    const v = (r?.value || "").toString().trim();
    if (!v) continue;
    custRows.push(`<div class="kv">${esc(r.label)}: ${esc(v)}</div>`);
  }
  if (notes) custRows.push(`<div class="kv">Σχόλια Παραγγελίας: <span style="font-style:italic">${esc(notes)}</span></div>`);
  const hasCustomerBlock = custRows.length > 0;

  // ----- Items list — Feb 2026 spec ------------------------------
  // Layout per item:
  //   qty = 1  →  single row:
  //     "1 X <description>" ............... "<unit_price>  <vat%>"
  //   qty > 1  →  two rows:
  //     row A:  "<qty> X <description>"                   (no prices)
  //     row B (right-aligned):  "<qty> X <unit_price> = <line_total>  <vat%>"
  //
  // Prices use Greek comma format. Comments/free_text are INTENTIONALLY
  // omitted from the proforma — they belong on the kitchen ticket where
  // the cook needs them, not on the customer/pass-slip.
  const fmt = (n) => Number(n || 0).toFixed(2).replace(".", ",");
  const itemsHtml = (items || [])
    .map((it) => {
      const qty = Number(it.qty || 1);
      const price = Number(it.price || 0);
      const desc = esc(it.description || "");
      const vat = it.vat_rate != null ? vatLabel(it.vat_rate) : "";
      const vatSpan = vat
        ? ` <span style="font-weight:normal;font-size:10px">${esc(vat)}</span>`
        : "";
      if (qty === 1) {
        return `
          <div style="display:flex;justify-content:space-between;font-size:12px;margin-top:3px;font-weight:bold">
            <span style="flex:1;padding-right:6px">1 X ${desc}</span>
            <span style="font-family:monospace;white-space:nowrap;text-align:right">${fmt(price)}${vatSpan}</span>
          </div>`;
      }
      const lineTotal = qty * price;
      const qtyDisplay = Number.isInteger(qty) ? String(qty) : String(qty);
      return `
        <div style="font-size:12px;margin-top:3px;font-weight:bold">${qtyDisplay} X ${desc}</div>
        <div style="text-align:right;font-family:monospace;font-size:11px;padding-right:0;white-space:nowrap">${qtyDisplay} X ${fmt(price)} = ${fmt(lineTotal)}${vatSpan}</div>`;
    })
    .join("");

  const html = `<!doctype html>
<html lang="el"><head><meta charset="utf-8"><title>Προφόρμα</title>
<style>
  @page { size: 76mm auto; margin: 0; }
  body { font-family: monospace, sans-serif; margin: 0; padding: 4mm; width: 68mm; font-size: 12px; color: #000; }
  .center { text-align: center; }
  .bold { font-weight: bold; }
  hr { border: 0; border-top: 1px dashed #000; margin: 6px 0; }
  .small { font-size: 10px; color: #333; }
  .code-big { font-size: 28px; font-weight: 900; font-family: monospace; text-align: center; letter-spacing: 0.1em; margin: 4px 0 2px; }
  .section-title { font-weight: bold; font-size: 11px; letter-spacing: 0.05em; margin-top: 4px; }
  .kv { font-size: 12px; line-height: 1.35; }
  .barcode-box { text-align: center; margin-top: 6px; }
  .barcode-box svg { width: 100%; max-width: 38mm; height: auto; }
  .barcode-val { font-family: monospace; font-size: 10px; letter-spacing: 0.1em; margin-top: 1px; }
  .non-fiscal { text-align:center; font-size:11px; font-weight:bold; margin-top:6px; padding:2px 0; border:1px dashed #000; }
  .powered { text-align:center; font-size:10px; color:#444; margin-top:4px; }
</style></head>
<body>
  ${businessHtml || '<div class="center small">— Στοιχεία επιχείρησης μη συμπληρωμένα —</div>'}
  ${title ? `<div class="center bold" style="font-size:13px;margin-top:4px">${esc(title)}</div>` : ""}
  ${code4 ? `<div class="code-big">#${code4}</div>` : ""}
  ${(globalOrderNo != null || dt) ? `<div class="center bold" style="font-size:12px;margin-top:2px">${globalOrderNo != null ? `Αρ.Παρ: ${esc(globalOrderNo)}` : ""}${(globalOrderNo != null && dt) ? "  " : ""}${dt ? esc(dt) : ""}</div>` : ""}
  ${tableName ? `<div class="center bold" style="font-size:13px;margin-top:3px">Τραπέζι: ${esc(tableName)}${batchNo != null ? ` · Παραγγελία #${esc(batchNo)}` : ""}</div>` : (batchNo != null ? `<div class="center small">Παραγγελία #${esc(batchNo)}</div>` : "")}
  ${hasCustomerBlock ? `
  <hr />
  <div class="section-title">ΣΤΟΙΧΕΙΑ ΠΕΛΑΤΗ</div>
  ${custRows.join("")}
  ` : ""}
  <hr />
  ${itemsHtml || '<div class="center small">(κενή παραγγελία)</div>'}
  <hr />
  <div style="display:flex;justify-content:space-between;font-size:16px" class="bold">
    <span>ΣΥΝΟΛΟ</span><span style="font-family:monospace">${fmt(total)} €</span>
  </div>
  ${driverName ? `<div class="small" style="margin-top:4px">Οδηγός: ${esc(driverName)}</div>` : ""}
  <div class="non-fiscal">ΔΕΝ ΑΠΟΤΕΛΕΙ ΦΟΡΟΛΟΓΙΚΟ ΣΤΟΙΧΕΙΟ</div>
  <div class="powered">Powered by tameiakes.gr</div>
  ${barcodeValue ? `<div class="barcode-box">
    <svg id="bc"></svg>
  </div>` : ""}
</body></html>`;

  return { html, barcodeValue };
}

/**
 * Spawn a print via a hidden iframe (same technique as Kiosk Mode).
 *
 * WHY IFRAME AND NEVER window.open?
 * ---------------------------------
 * The proforma print button is always called AFTER at least one
 * ``await http.post(...)`` (order-number reservation, customer
 * lookup, …). The intervening awaits consume the user-activation
 * token, so browsers on the production HTTPS deployment silently
 * block ``window.open("", "_blank", …)`` — the caller sees the
 * successful return of an empty proforma window that never renders,
 * shows a green ✓ toast, and no paper comes out. Worse, the blocked
 * popup can steal focus and freeze the parent tab until reload.
 *
 * The hidden-iframe path has none of those failure modes and works
 * identically on regular Chrome (print preview), Kiosk with
 * ``--kiosk-printing`` (silent), and inside a --kiosk shell (no
 * popup allowed at all).
 */
export async function openProformaWindow({ html, barcodeValue }) {
  let finalHtml = html;
  // Barcode rendering: JsBarcode needs a real DOM node — we generate
  // one in the parent document, then serialize its outerHTML into the
  // template BEFORE handing the HTML to the iframe. This avoids the
  // async load race that the previous popup-based version had.
  if (barcodeValue) {
    try {
      const { default: JsBarcode } = await import("jsbarcode");
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      JsBarcode(svg, barcodeValue, {
        format: "CODE128",
        height: 20,
        displayValue: false,
        margin: 0,
        background: "#fff",
      });
      finalHtml = finalHtml.replace('<svg id="bc"></svg>', svg.outerHTML);
    } catch {
      /* jsbarcode failed — print without barcode; caller already
         has the numeric code in the header so this is acceptable. */
    }
  }
  return printHtmlSilent(finalHtml, { afterMs: 350 });
}

/** Convenience — build + print in a single call. */
export async function printProforma(ctx) {
  const built = buildProformaHtml(ctx);
  return openProformaWindow(built);
}
